import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { SettingsLayout } from "@/components/dashboard/SettingsLayout";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { 
  ArrowLeft, 
  Loader2, 
  User,
  Shield,
  CreditCard,
  Eye,
  EyeOff,
  Check,
  X,
  Link2
} from "lucide-react";

const SecuritySettings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isChanging, setIsChanging] = useState(false);

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast({
        title: t("settings.security.error"),
        description: t("auth.errors.passwordMismatch"),
        variant: "destructive",
      });
      return;
    }

    if (newPassword.length < 8) {
      toast({
        title: t("settings.security.error"),
        description: t("auth.errors.passwordMin"),
        variant: "destructive",
      });
      return;
    }

    try {
      setIsChanging(true);

      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      toast({
        title: t("settings.security.passwordChanged"),
        description: t("settings.security.passwordChangedDesc"),
      });

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      console.error("Password change error:", error);
      toast({
        title: t("settings.security.error"),
        description: t("settings.security.passwordError"),
        variant: "destructive",
      });
    } finally {
      setIsChanging(false);
    }
  };

  // Check if user signed up with social provider
  const isGoogleConnected = user?.app_metadata?.provider === "google";

  const isEmailUser = user?.app_metadata?.provider === "email";

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold">{t("settings.title")}</h1>
        <p className="text-muted-foreground">{t("settings.subtitle")}</p>
      </div>

      <SettingsLayout>
          {/* Change Password */}
          <Card>
            <CardHeader>
              <CardTitle>{t("settings.security.changePassword")}</CardTitle>
              <CardDescription>{t("settings.security.changePasswordDesc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isEmailUser ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">{t("settings.security.currentPassword")}</Label>
                    <div className="relative">
                      <Input
                        id="currentPassword"
                        type={showCurrentPassword ? "text" : "password"}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder={t("settings.security.passwordPlaceholder")}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      >
                        {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="newPassword">{t("settings.security.newPassword")}</Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder={t("settings.security.passwordPlaceholder")}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">{t("settings.security.confirmPassword")}</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder={t("settings.security.passwordPlaceholder")}
                    />
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button onClick={handleChangePassword} disabled={isChanging}>
                      {isChanging ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t("settings.saving")}
                        </>
                      ) : (
                        t("settings.security.updatePassword")
                      )}
                    </Button>
                  </div>
                </>
              ) : (
                <p className="text-muted-foreground">
                  {t("settings.security.socialPasswordNote")}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Connected Accounts */}
          <Card>
            <CardHeader>
              <CardTitle>{t("settings.security.connectedAccounts")}</CardTitle>
              <CardDescription>{t("settings.security.connectedAccountsDesc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Google */}
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center border">
                    <svg viewBox="0 0 24 24" className="w-5 h-5">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">{t("settings.security.google")}</p>
                    {isGoogleConnected && (
                      <p className="text-sm text-muted-foreground">{user?.email}</p>
                    )}
                  </div>
                </div>
                {isGoogleConnected ? (
                  <Badge className="bg-green-500 flex items-center gap-1">
                    <Check className="w-3 h-3" />
                    {t("settings.security.connected")}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="flex items-center gap-1">
                    <X className="w-3 h-3" />
                    {t("settings.security.notConnected")}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Last Login */}
          <Card>
            <CardHeader>
              <CardTitle>{t("settings.security.loginInfo")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-muted-foreground">
                <p>{t("settings.security.lastLogin")}: {user?.last_sign_in_at 
                  ? new Date(user.last_sign_in_at).toLocaleString() 
                  : t("settings.security.unknown")
                }</p>
              </div>
            </CardContent>
          </Card>
      </SettingsLayout>
    </div>
  );
};

export default SecuritySettings;
